import { ReactNode, useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SysRecord from "app/class/record";
import Translate from "component/global/translation/translation-component";
import { formatActionShortcut, getActionShortcut } from "modules/record/shared/services/actionHotkeys";
import { getActionButtonSx, getActionIconSx, getActionMuiColor } from "modules/record/shared/services/actionStyles";
import { useTranslation } from "react-i18next";

type ResponsiveActionBarVariant = "form" | "toolbar" | "inline";

interface ResponsiveActionBarProps {
    actions: SysRecord[];
    runningActionId?: string | null;
    disabled?: boolean;
    menuLabel?: string;
    mobileMenuLabel?: string;
    showMobileMenuLabel?: boolean;
    mobileVisibleCount?: number;
    desktopVisibleCount?: number;
    variant?: ResponsiveActionBarVariant;
    leadingContent?: ReactNode;
    getActionLabel?: (action: SysRecord) => string | undefined;
    trailingContent?: ReactNode;
    getActionDisabled?: (action: SysRecord) => boolean;
    onActionClick: (action: SysRecord) => void;
}

const actionLabel = (action: SysRecord) => action.label || "Action";

const actionShortcutLabel = (action: SysRecord) => formatActionShortcut(getActionShortcut(action));

export const getFormActionPresentation = (action: SysRecord) => {
    if (action.success_navigation === "stay") {
        return { label: "record.producer.action.save_and_stay", tooltip: "record.producer.action.save_and_stay.tooltip", testId: "record-producer-action-save-stay", variant: "contained" as const };
    }

    if (action.success_navigation === "return_to" && action.success_navigation_fallback === "table_list") {
        return { label: "record.producer.action.update_and_return", tooltip: "record.producer.action.update_and_return.tooltip", testId: "record-producer-action-update-return", variant: "outlined" as const };
    }

    return null;
};

const mobileDrawerBottomGap = "calc(16px + env(safe-area-inset-bottom))";

const ResponsiveActionBar = ({
    actions,
    runningActionId,
    disabled = false,
    menuLabel = "More",
    mobileMenuLabel,
    showMobileMenuLabel = false,
    mobileVisibleCount = 1,
    desktopVisibleCount,
    variant = "toolbar",
    leadingContent,
    getActionLabel,
    trailingContent,
    getActionDisabled,
    onActionClick,
}: ResponsiveActionBarProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const menuOpen = Boolean(menuAnchorEl);

    const visibleCount = isMobile
        ? mobileVisibleCount
        : desktopVisibleCount ?? actions.length;

    const { visibleActions, overflowActions } = useMemo(() => ({
        visibleActions: actions.slice(0, Math.max(visibleCount, 0)),
        overflowActions: actions.slice(Math.max(visibleCount, 0)),
    }), [actions, visibleCount]);

    const isActionDisabled = (action: SysRecord) =>
        disabled || Boolean(runningActionId) || Boolean(getActionDisabled?.(action));

    const closeMenu = () => setMenuAnchorEl(null);

    const handleOverflowActionClick = (action: SysRecord) => {
        closeMenu();
        onActionClick(action);
    };

    if (!actions.length && !trailingContent && !leadingContent) {
        return null;
    }

    const overflowButton = overflowActions.length > 0 && (
        <Tooltip title={String(menuLabel)}>
            <span>
                <Button
                    variant="contained"
                    color="inherit"
                    size="small"
                    disabled={disabled || Boolean(runningActionId)}
                    onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                    endIcon={isMobile && !showMobileMenuLabel ? undefined : <ExpandMoreIcon fontSize="small" />}
                    sx={{
                        minWidth: { xs: showMobileMenuLabel ? 92 : 44, sm: 0 },
                        height: { xs: 40, sm: 32 },
                        px: { xs: 1, sm: 1.25 },
                        whiteSpace: "nowrap",
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        // border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "none",
                        // MUI's contained/inherit hover is a fixed light grey (#F5F5F5): unreadable with the light
                        // Dark-theme text. Keep the themed paper and layer the translucent hover overlay on top.
                        "&:hover": {
                            backgroundColor: "background.paper",
                            backgroundImage: (theme) => `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                            boxShadow: "none",
                        },
                        // Disabled must read as disabled: the theme's disabled text token (~2.8:1, distinct from
                        // secondary text) on the same paper, instead of high-contrast text that looked enabled.
                        "&.Mui-disabled": {
                            backgroundColor: "background.paper",
                            color: "var(--prm-text-disabled)",
                            borderColor: "divider",
                            opacity: 1,
                            "& .MuiTypography-root, & .MuiSvgIcon-root": {
                                color: "var(--prm-text-disabled)",
                            },
                        },
                    }}
                >
                    {isMobile && !showMobileMenuLabel ? (
                        <MoreHorizIcon fontSize="small" />
                    ) : (
                        <Translate text={isMobile ? (mobileMenuLabel || menuLabel) : menuLabel} fontSize="small" />
                    )}
                </Button>
            </span>
        </Tooltip>
    );

    const overflowList = (
        <List disablePadding dense={!isMobile}>
            {overflowActions.map((action) => {
                const running = runningActionId === action.id;
                const itemDisabled = isActionDisabled(action);

                if (isMobile) {
                    return (
                        <ListItemButton
                            key={action.id}
                            disabled={itemDisabled}
                            onClick={() => handleOverflowActionClick(action)}
                            sx={{ minHeight: 48 }}
                        >
                            <ListItemIcon sx={(theme) => ({ minWidth: 40, ...getActionIconSx(action)(theme) })}>
                                {running ? <CircularProgress size={18} color="inherit" /> : <BoltIcon fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText primary={<Translate text={getActionLabel?.(action) || actionLabel(action)} />} />
                        </ListItemButton>
                    );
                }

                return (
                    <Tooltip key={action.id} title={actionShortcutLabel(action)} placement="right">
                        <span>
                            <MenuItem
                                disabled={itemDisabled}
                                onClick={() => handleOverflowActionClick(action)}
                            >
                                <ListItemIcon sx={getActionIconSx(action)}>
                                    {running ? <CircularProgress size={16} color="inherit" /> : <BoltIcon fontSize="small" />}
                                </ListItemIcon>
                                <Translate text={getActionLabel?.(action) || actionLabel(action)} fontSize="small" />
                            </MenuItem>
                        </span>
                    </Tooltip>
                );
            })}
        </List>
    );

    return (
        <Box
            sx={{
                bgcolor: variant === "inline" ? "transparent" : "var(--prm-secondary)",
                "--prm-accent-on-surface": variant === "inline" ? undefined : "var(--prm-accent-on-secondary)",
                "--prm-outlined-border": variant === "inline" ? undefined : "var(--prm-accent-on-secondary)",
                color: variant === "inline" ? "text.primary" : "var(--prm-primary-contrast)",
                borderRadius: variant === "inline" ? 0 : (themeValue) => `${themeValue.shape.borderRadius}px`,
                // border: variant === "inline" ? 0 : "1px solid rgba(255, 255, 255, 0.16)",
                boxShadow: variant === "form" ? (themeValue) => themeValue.shadows[1] : 0,
                position: variant === "form" ? { xs: "sticky", sm: "static" } : "static",
                bottom: variant === "form" ? { xs: 8, sm: "auto" } : "auto",
                zIndex: variant === "form" ? (themeValue) => themeValue.zIndex.appBar : "auto",
                m: variant === "form" ? { xs: "10px 0.5px", sm: "4px 0.5px" } : 0,
                px: variant === "inline" ? 0 : { xs: 1, sm: "4px 0.5px" },
                py: variant === "inline" ? 0 : { xs: 1, sm: "4px 0.5px" },
                pb: variant === "form" ? { xs: "calc(8px + env(safe-area-inset-bottom))", sm: "4px 0.5px" } : undefined,
                overflow: "hidden",
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                    minHeight: variant === "inline" ? "auto" : { xs: 44, sm: 36 },
                    overflow: "hidden",
                }}
            >
                {leadingContent && <Box sx={{ display: "inline-flex", flex: "0 0 auto", mr: "auto" }}>{leadingContent}</Box>}
                {visibleActions.map((action) => {
                    const running = runningActionId === action.id;
                    const labelOverride = getActionLabel?.(action);
                    const formPresentation = variant === "form" && !labelOverride ? getFormActionPresentation(action) : null;
                    const shortcut = actionShortcutLabel(action);
                    const tooltip = formPresentation
                        ? String(t(formPresentation.tooltip)) + (shortcut ? " (" + shortcut + ")" : "")
                        : shortcut;

                    return (
                        <Tooltip key={action.id} title={tooltip}>
                            <span
                                style={{
                                    display: "inline-flex",
                                    flex: isMobile ? "1 1 auto" : "0 0 auto",
                                    maxWidth: isMobile ? 180 : undefined,
                                }}
                            >
                                <Button
                                    size="small"
                                    data-testid={formPresentation?.testId}
                                    variant={formPresentation?.variant || "contained"}
                                    color={getActionMuiColor(action)}
                                    disabled={isActionDisabled(action)}
                                    startIcon={running ? <CircularProgress size={14} color="inherit" /> : undefined}
                                    onClick={() => onActionClick(action)}
                                    sx={(theme) => ({
                                        minWidth: { xs: 0, sm: 64 },
                                        height: { xs: 40, sm: 32 },
                                        width: "100%",
                                        maxWidth: "100%",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        ...getActionButtonSx(action)(theme),
                                        ...(formPresentation?.variant === "outlined" ? {
                                            backgroundColor: "transparent",
                                            color: "var(--prm-accent-on-secondary)",
                                            borderColor: "var(--prm-accent-on-secondary)",
                                            "&:hover": {
                                                backgroundColor: theme.palette.action.hover,
                                                borderColor: "var(--prm-accent-on-secondary)",
                                                boxShadow: "none",
                                            },
                                        } : {}),
                                    })}
                                >
                                    <Translate text={labelOverride || formPresentation?.label || actionLabel(action)} fontSize="small" />
                                </Button>
                            </span>
                        </Tooltip>
                    );
                })}
                {overflowButton}
                {trailingContent}
            </Stack>

            {isMobile ? (
                <Drawer
                    anchor="bottom"
                    open={menuOpen}
                    onClose={closeMenu}
                    PaperProps={{
                        sx: {
                            bottom: mobileDrawerBottomGap,
                            mx: 1,
                            width: "calc(100% - 16px)",
                            pb: 1,
                            borderRadius: 1,
                            overflow: "hidden",
                        },
                    }}
                >
                    {overflowList}
                </Drawer>
            ) : (
                <Menu open={menuOpen} anchorEl={menuAnchorEl} onClose={closeMenu}>
                    {overflowList}
                </Menu>
            )}
        </Box>
    );
};

export default ResponsiveActionBar;
